/**
 * Language utilities for persisting and retrieving user language preferences
 */

export type Locale = 'en' | 'zh' | 'ms' | 'ta' | 'th'

const STORAGE_KEY = 'app.locale'

/**
 * Get stored locale from localStorage
 */
export function getStoredLocale(): Locale | null {
  if (typeof window === 'undefined') return null

  const stored = window.localStorage.getItem(STORAGE_KEY)

  // Validate that stored value is a valid locale
  if (
    stored === 'en' ||
    stored === 'zh' ||
    stored === 'ms' ||
    stored === 'ta' ||
    stored === 'th'
  ) {
    return stored
  }

  return null
}

/**
 * Store locale preference in localStorage
 */
export function setStoredLocale(locale: Locale): void {
  if (typeof window === 'undefined') return

  window.localStorage.setItem(STORAGE_KEY, locale)
}

/**
 * Get browser's preferred language, mapped to our supported locales
 */
export function getBrowserLocale(): Locale {
  if (typeof window === 'undefined') return 'en'

  const browserLang = navigator.language.toLowerCase()

  // Map browser languages to our supported locales
  if (browserLang.startsWith('zh')) return 'zh'
  if (browserLang.startsWith('ms')) return 'ms'
  if (browserLang.startsWith('ta')) return 'ta'
  if (browserLang.startsWith('th')) return 'th'

  return 'en' // Default fallback
}

/**
 * Get locale display name for UI
 */
export function getLocaleDisplayName(locale: Locale): string {
  const names: Record<Locale, string> = {
    en: 'English',
    zh: '中文',
    ms: 'Bahasa Melayu',
    ta: 'தமிழ்',
    th: 'ไทย',
  }

  return names[locale] || names.en
}
